import type { Location, PublicCity, Station } from "@/lib/api";

const STATION_RADIUS_KM = 15;
const CITY_RADIUS_KM = 25;

export function haversineKm(
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
): number {
	const R = 6371;
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLon / 2) ** 2;
	return 2 * R * Math.asin(Math.sqrt(a));
}

function isCovered(
	lat: number,
	lon: number,
	stations: Station[],
	city: PublicCity | null,
): boolean {
	for (const station of stations) {
		if (haversineKm(lat, lon, station.lat, station.lon) <= STATION_RADIUS_KM) {
			return true;
		}
	}
	if (city?.lat != null && city?.lon != null) {
		return haversineKm(lat, lon, city.lat, city.lon) <= CITY_RADIUS_KM;
	}
	return true;
}

/**
 * Returns a user-facing warning when an endpoint is likely outside the
 * route coverage area, or null when both endpoints look covered.
 */
export function getCoverageWarning(
	from: Location | null,
	to: Location | null,
	stations: Station[],
	city: PublicCity | null,
): string | null {
	if (!from || !to) return null;
	const fromCovered = isCovered(from.latitude, from.longitude, stations, city);
	const toCovered = isCovered(to.latitude, to.longitude, stations, city);
	if (!fromCovered && !toCovered) {
		return "Neither endpoint looks covered yet — results may be limited.";
	}
	if (!fromCovered) {
		return `"${from.name}" looks outside our route coverage — results may be limited.`;
	}
	if (!toCovered) {
		return `"${to.name}" looks outside our route coverage — results may be limited.`;
	}
	return null;
}
